/**
 * features/timetable/MyTimetable.tsx
 * ------------------------------------------------------------------
 * The portal's timetable screen. Read-only — authoring lives in the
 * standalone timetable app (see TIMETABLE-INTEGRATION-PLAN.md).
 *
 * It shows one grid, but WHOSE grid depends on who is looking:
 *
 *   a teacher   their own lessons, from the linked teacher record
 *   a parent    their child's class (with a child picker for siblings)
 *   a student   their own class, resolved from studentAccount.className
 *   staff       any class, teacher or room, via a picker
 *
 * Everyone with `canViewAllTimetables` (LKC: admin, head, academic admins,
 * HODs, HOYs) gets every picker. Any other teacher (the school, 31 Oct 2026)
 * gets their own grid and a class picker of THEIR classes only: the classes
 * they teach on the timetable, and the class they are (assistant) class
 * teacher of. Pupils and parents get exactly their own grid.
 * ------------------------------------------------------------------ */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { subjectFamily, FAMILY_LABELS, LEGEND_FAMILIES, type SubjectFamily } from '@/lib/subjectFamily';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import {
  phaseDivisionsOfTeacher,
  divisionOfClass,
  divisionsOfTeacher,
  type Division,
} from '@/lib/divisions';
import { Calendar, Download, Printer, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useTimetableData } from './useTimetableData';
import RecentChanges from './RecentChanges';
import { tidySubjectLabel, type SubjectNamer } from '@/lib/liveTimetable';
import { MyDay, SchoolNow } from './LiveViews';
import { isIslamicSubject } from '@/lib/islamicRegister';
import { Page, PageHeader, Tabs, btnGhost, btnPdf } from '@/components/ui/design';
import {
  DAYS,
  classGrid,
  teacherGrid,
  roomGrid,
  slotLength,
  groupsForStudent,
  groupsForTeacher,
  classesByTeacher,
  teacherPhases,
  slotGroups,
  compareBySurname,
  type PupilContext,
  type TeacherPhase,
  coTeachers,
  isTeachingSlot,
  type Grid,
  type PeriodSlot,
  type ScheduleSettings,
  type SlotGroup,
  type TimetableSlot,
} from '@/lib/timetable';

type Mode = 'mine' | 'class' | 'teacher' | 'room';
/** The screen's top level: a teacher's day now, the school now, or the week grids. */
type View = 'myday' | 'school' | 'week';

/** What `getMyTimetableContext` returns: the pupil, and the names they may see. */
interface PupilTimetableContext extends PupilContext {
  className: string;
  teacherNames: Record<string, string>;
}

/*
 * Individual timetables are SWITCHED OFF until lesson registers are in use.
 * Sets (Physics L or M) are recorded under Lesson Register -> Teaching groups,
 * and on 2026-09-14 none had been, so a personalised grid would have shown
 * "set not confirmed" in every option block. The school chose to keep pupils
 * on the whole class timetable until then (a parent's view still narrows to
 * the child's subjects, as it always has). Teacher NAMES still come from
 * getMyTimetableContext either way. Flip this to true to turn it on.
 */
const PERSONALISED_PUPIL_TIMETABLE = false;

const loadPupilContext = httpsCallable<{ studentId?: string }, PupilTimetableContext>(
  functions, 'getMyTimetableContext',
);

/** Remembered across visits: an office that prints primary timetables does so every time. */
const PHASE_KEY = 'lkc.timetable.teacherPhase';

/** `bare`: inside the parent app, which supplies the page's title. */
export default function MyTimetable({ bare = false }: { bare?: boolean } = {}) {
  const { userData, permissions: perms, userRoles } = useAuth();
  const { teachers, classes, students, subjects } = useData();

  const isParent = userRoles.includes('parent');
  const myTeacherId = userData?.linkedTeacherId ?? null;

  /* A student-only session reads none of the core collections — no classes, no
   * subjects, no teachers — so this screen cannot look anything up. It does not
   * need to: timetable slots are keyed by the class's DISPLAY NAME ("Form 5A"),
   * which is exactly what the account link already carries. Before this branch
   * existed the mode fell through to 'class' with nothing picked, and the grid
   * rendered empty for every student. */
  const isStudentOnly = userRoles.includes('student')
    && !userRoles.some((r) => r !== 'student');
  const myClassName = userData?.studentAccount?.className ?? '';

  /* --- who am I looking at? ------------------------------------------- */
  const myChildren = useMemo(
    () => (isParent ? students.filter((s) => (userData?.parentAccount?.studentIds ?? []).includes(s.id)) : []),
    [isParent, students, userData],
  );

  const [mode, setMode] = useState<Mode>(
    myTeacherId || isParent || isStudentOnly ? 'mine' : 'class',
  );
  const [childId, setChildId] = useState<string>(myChildren[0]?.id ?? '');
  const [pickedClass, setPickedClass] = useState<string>('');
  const [pickedTeacher, setPickedTeacher] = useState<string>('');
  const [pickedRoom, setPickedRoom] = useState<string>('');

  const child = myChildren.find((s) => s.id === childId) ?? myChildren[0] ?? null;
  const childClass = child ? classes.find((c) => c.id === child.currentClass) : null;

  /**
   * A parent loads only their child's class; staff load the school. Passing a
   * class name here is what keeps a parent's read to ~52 pointers instead of
   * 832 — see useTimetableData.
   */
  const scopeClass = isParent && !perms.canViewAllTimetables
    // A restricted parent must NEVER fall through to the whole-school load, so
    // when the child's class cannot be resolved we scope to a name that matches
    // nothing rather than to null. Most primary classes have no timetable yet,
    // so this is the common path, not an edge case.
    ? (childClass?.name ?? ' none')
    : null;
  /**
   * Which bell schedule this grid is drawn on.
   *
   * Primary runs thirteen periods against secondary's eleven and disagrees
   * about when everything after period two starts, so loading secondary's
   * bells for a primary class silently drops its last two lessons of the day
   * and mislabels the rest. A class's division is its stored field, never
   * derived from its name (lib/divisions).
   *
   * A class grid has exactly one division. A teacher's comes from their
   * record. A room, and a teacher who crosses divisions, have no single right
   * answer, so they keep the default.
   */
  const viewDivision: Division = useMemo(() => {
    const ofClass = (name?: string | null) =>
      divisionOfClass(classes.find((c) => c.name === name));

    if (mode === 'mine' && isParent) return ofClass(childClass?.name) ?? 'secondary';
    if (mode === 'mine' && isStudentOnly) return ofClass(myClassName) ?? 'secondary';
    if (mode === 'class' && pickedClass) return ofClass(pickedClass) ?? 'secondary';

    const teacherId = mode === 'teacher' ? pickedTeacher : mode === 'mine' ? myTeacherId : null;
    if (teacherId) {
      const divs = divisionsOfTeacher(teachers.find((t) => t.id === teacherId));
      const phases = new Set(divs.map((d) => (d === 'secondary' ? 'secondary' : 'primary')));
      if (phases.size === 1 && divs.length) return divs[0];
    }
    return 'secondary';
  }, [mode, isParent, isStudentOnly, childClass, myClassName, pickedClass,
      pickedTeacher, myTeacherId, classes, teachers]);

  const { slots, pointers, schedule, rooms, loading, error } =
    useTimetableData(scopeClass, viewDivision);

  /*
   * A pupil's own grid, personalised.
   *
   * A student or parent session cannot read `reportTeachers` (phones, emails)
   * or `timetableGroupMembership` (staff-only), which is why pupils saw raw
   * teacher ids and both Physics sets with nothing to say which was theirs.
   * The callable resolves the pupil server-side and hands back only the
   * display names on their class's timetable, their subjects and any set a
   * teacher has recorded.
   */
  const pupilSubjectFallback = (child as { subjects?: string[] } | null)?.subjects ?? [];
  const wantsPupil = mode === 'mine' && (isStudentOnly || (isParent && !!child));
  const [pupilCtx, setPupilCtx] = useState<PupilTimetableContext | null>(null);
  useEffect(() => {
    if (!wantsPupil) { setPupilCtx(null); return; }
    let live = true;
    loadPupilContext(isParent && child ? { studentId: child.id } : {})
      .then((r) => { if (live) setPupilCtx(r.data); })
      // No context is not an error the pupil can act on: the grid still shows
      // every lesson in each block, exactly as it did before this existed.
      .catch(() => { if (live) setPupilCtx(null); });
    return () => { live = false; };
  }, [wantsPupil, isParent, child?.id]);

  const codeOf = useMemo(() => {
    const m = new Map<string, string>();
    teachers.forEach((t) => m.set(t.id, (t as { timetableCode?: string }).timetableCode ?? ''));
    return (id: string) => m.get(id) || undefined;
  }, [teachers]);

  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    teachers.forEach((t) => m.set(t.id, t.displayName || t.fullName));
    // A pupil session has no teachers loaded; the callable supplies the names.
    Object.entries(pupilCtx?.teacherNames ?? {}).forEach(([id, n]) => { if (n) m.set(id, n); });
    return (id: string) => m.get(id) ?? id;
  }, [teachers, pupilCtx]);

  /* Pupils and parents read NAMES; a two-letter code means nothing to someone
     who has never seen the staff list. Staff keep the codes they work in. */
  const preferNames = wantsPupil;

  const subjectName = useMemo(() => {
    const m = new Map<string, string>();
    subjects.forEach((s) => m.set(s.id, s.name));
    return (id?: string | null) => (id ? m.get(id) : undefined);
  }, [subjects]);

  /* --- build the grid --------------------------------------------------- */
  const { grid, heading, subheading, pupil, focusTeacherId } = useMemo((): {
    grid: Grid | null; heading: string; subheading: string;
    pupil: PupilContext | null; focusTeacherId: string | null;
  } => {
    const none = { grid: null, heading: '', subheading: '', pupil: null, focusTeacherId: null };

    if (isParent && mode === 'mine') {
      if (!child || !childClass) return { ...none, heading: 'My child' };
      return {
        grid: classGrid(childClass.name, pointers, slots),
        heading: child.fullName,
        subheading: childClass.name,
        pupil: PERSONALISED_PUPIL_TIMETABLE
          ? pupilCtx ?? { subjectIds: pupilSubjectFallback, streams: {} }
          : { subjectIds: pupilSubjectFallback, streams: {} },
        focusTeacherId: null,
      };
    }
    if (isStudentOnly && mode === 'mine') {
      if (!myClassName) return { ...none, heading: 'Your timetable' };
      return {
        grid: classGrid(myClassName, pointers, slots),
        heading: myClassName,
        subheading: 'Your timetable',
        pupil: PERSONALISED_PUPIL_TIMETABLE ? pupilCtx : null,
        focusTeacherId: null,
      };
    }
    if (mode === 'mine' && myTeacherId) {
      return {
        grid: teacherGrid(myTeacherId, slots),
        heading: nameOf(myTeacherId),
        subheading: 'My lessons',
        pupil: null,
        // Narrows every option block to this teacher's own group.
        focusTeacherId: myTeacherId,
      };
    }
    if (mode === 'teacher' && pickedTeacher) {
      return {
        grid: teacherGrid(pickedTeacher, slots), heading: nameOf(pickedTeacher),
        subheading: 'Teacher timetable', pupil: null, focusTeacherId: pickedTeacher,
      };
    }
    if (mode === 'room' && pickedRoom) {
      return {
        grid: roomGrid(pickedRoom, slots), heading: pickedRoom,
        subheading: 'Room timetable', pupil: null, focusTeacherId: null,
      };
    }
    if (mode === 'class' && pickedClass) {
      return {
        grid: classGrid(pickedClass, pointers, slots), heading: pickedClass,
        subheading: 'Class timetable', pupil: null, focusTeacherId: null,
      };
    }
    return none;
  }, [isParent, isStudentOnly, myClassName, mode, child, childClass, myTeacherId,
    pickedTeacher, pickedRoom, pickedClass, pointers, slots, nameOf, pupilCtx]);

  /* --- export -----------------------------------------------------------
   * The authoring app's printout (the school, 2026-10-20): A3 landscape, a
   * Monday–Thursday grid and a Friday grid on their own bells, three lines a
   * cell. lib/timetableWeekPdf is a port of that app's engine, imported on
   * the click because jsPDF must not reach the first load.
   * -------------------------------------------------------------------- */
  async function exportPdf() {
    if (!grid || !schedule) return;
    const m = await import('@/lib/timetableWeekPdf');
    const naming = {
      teacherLabel: (t: string) => {
        const n = preferNames ? nameOf(t) : codeOf(t) || nameOf(t);
        // A pupil's names come from a callable; never print an internal id.
        return /^teacher_\d+$/.test(n) ? '' : n;
      },
      subjectOf: (g: SlotGroup) => (g.kind === 'supervision'
        ? 'Supervised study'
        : subjectName(g.subjectId) ?? tidySubjectLabel(g.subjectLabel, g.streamLabel) ?? ''),
    };
    let page;
    if (focusTeacherId) {
      const code = codeOf(focusTeacherId);
      page = m.teacherWeekPage({
        teacherId: focusTeacherId, grid, schedule, naming,
        title: `${nameOf(focusTeacherId)}${code ? ` (${code})` : ''}`,
      });
    } else if (mode === 'room' && pickedRoom) {
      page = m.roomWeekPage({ room: pickedRoom, grid, schedule, naming });
    } else {
      page = m.classWeekPage({
        grid, schedule, naming, pupil,
        title: heading,
        // A parent's heading is the child's name; the class goes underneath.
        subtitle: isParent && mode === 'mine' && subheading ? `${subheading} · Class Timetable` : 'Class Timetable',
      });
    }
    m.downloadTimetableWeekPdf([page], `timetable-${heading.replace(/\s+/g, '-').toLowerCase()}`);
  }

  /*
   * Primary or secondary, for the teacher picker.
   *
   * From HR's divisions UNIONED with the classes the teacher is actually
   * timetabled against, so a teacher who works in both parts of the school is
   * in both lists — see `teacherPhases`. Sorted by surname, not by the display
   * name, which begins with a title and so ordered every Mr before every Ms.
   */
  const [phase, setPhaseState] = useState<'all' | TeacherPhase>(() => {
    try {
      const v = localStorage.getItem(PHASE_KEY);
      return v === 'primary' || v === 'secondary' || v === 'islamic' ? v : 'all';
    } catch { return 'all'; }
  });
  const setPhase = (v: 'all' | TeacherPhase) => {
    setPhaseState(v);
    try { localStorage.setItem(PHASE_KEY, v); } catch { /* private mode: fine */ }
  };
  const teacherPhaseById = useMemo(() => {
    const taught = classesByTeacher(slots);
    /* ISLAMIC (the school, 2026-10-27): the Islamic division on the record, OR
       an Islamic lesson on the timetable (isIslamicSubject, never Arabic). */
    const islamicLesson = new Set<string>();
    for (const slot of Object.values(slots)) {
      for (const g of slotGroups(slot)) if (isIslamicSubject(g.subjectLabel)) (g.teacherIds ?? []).forEach((id) => islamicLesson.add(id));
    }
    const m = new Map<string, Set<TeacherPhase>>();
    teachers.forEach((t) => {
      const ph = teacherPhases(phaseDivisionsOfTeacher(t), taught.get(t.id) ?? []);
      if (islamicLesson.has(t.id)) ph.add('islamic');
      m.set(t.id, ph);
    });
    return m;
  }, [teachers, slots]);
  const phaseCount = (p: TeacherPhase) => teachers.filter((t) => teacherPhaseById.get(t.id)?.has(p)).length;
  const teacherOptions = useMemo(
    () => teachers
      .filter((t) => phase === 'all' || teacherPhaseById.get(t.id)?.has(phase))
      .slice()
      .sort(compareBySurname)
      .map((t) => ({ value: t.id, label: t.displayName || t.fullName })),
    [teachers, phase, teacherPhaseById],
  );

  /* --- render ----------------------------------------------------------- */
  const canLookUp = perms.canViewAllTimetables;
  /* A teacher's own classes: wherever they appear on the timetable (a group
     spanning several classes counts for each), and their class-teacher class. */
  const myClassNames = useMemo(() => {
    const out = new Set<string>();
    if (!myTeacherId) return out;
    for (const slot of Object.values(slots)) {
      if (slotGroups(slot).some((g) => (g.teacherIds ?? []).includes(myTeacherId))) slot.classes.forEach((c) => out.add(c));
    }
    const me = teachers.find((t) => t.id === myTeacherId);
    const own = [me?.classTeacherOf, ...(me?.assistantClassTeacherOf ?? [])].filter(Boolean) as string[];
    classes.filter((c) => own.includes(c.id)).forEach((c) => out.add(c.name));
    return out;
  }, [myTeacherId, slots, teachers, classes]);
  const classChoices = classes.filter((c) => !c.archived && (canLookUp || myClassNames.has(c.name)));
  const canPickClass = canLookUp || (!!myTeacherId && classChoices.length > 0);

  /* THE SCREEN OPENS ON NOW (the school, 2026-09-29): a teacher on their own
     day, anyone who may look up any timetable on the whole school, pupils and
     parents on the week as before. */
  const views: { id: View; label: string }[] = [
    ...(myTeacherId && !isStudentOnly ? [{ id: 'myday' as const, label: 'My day' }] : []),
    ...(canLookUp ? [{ id: 'school' as const, label: 'School now' }] : []),
    { id: 'week', label: myTeacherId ? 'Week and look-up' : 'Week' },
  ];
  const [view, setView] = useState<View>(views[0].id);
  const subjectOf: SubjectNamer = useMemo(() => (g) => (g.kind === 'supervision'
    ? 'Supervised study' : subjectName(g.subjectId) ?? tidySubjectLabel(g.subjectLabel, g.streamLabel)), [subjectName]);
  const phaseOf = useMemo(() => (id: string) => teacherPhaseById.get(id), [teacherPhaseById]);

  return (
    <Page wide={!bare} bare={bare}>
    <div className="space-y-4">
      {bare ? (view === 'week' && grid ? (
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => { void exportPdf(); }} className={btnPdf}><Download className="h-4 w-4" /> PDF</button>
        </div>
      ) : null) : <PageHeader
        icon={Calendar}
        eyebrow="Timetable"
        title={view === 'myday' ? 'My day' : view === 'school' ? 'Where everyone is' : 'Timetable'}
        subtitle={view === 'week' ? 'Read-only. Alterations are made in the timetable system.' : 'Live from the bell schedule, Gaborone time.'}
        actions={view === 'week' && grid ? (
          <>
            <button type="button" onClick={() => { void exportPdf(); }} className={btnPdf}><Download className="h-4 w-4" /> PDF</button>
            <button type="button" onClick={() => window.print()} className={btnGhost}><Printer className="h-4 w-4" /> Print</button>
          </>
        ) : undefined}
      />}

      {views.length > 1 && <Tabs<View> value={view} onChange={setView} items={views} />}

      {view === 'myday' && myTeacherId && (
        loading ? <Empty>Loading the timetable…</Empty>
          : <MyDay teacherId={myTeacherId} slots={slots} schedule={schedule} nameOf={nameOf} subjectOf={subjectOf} onOpenWeek={() => { setMode('mine'); setView('week'); }} />
      )}
      {view === 'school' && (
        loading ? <Empty>Loading the timetable…</Empty>
          : <SchoolNow slots={slots} pointers={pointers} schedule={schedule} rooms={rooms} teachers={teachers}
              nameOf={nameOf} codeOf={codeOf} subjectOf={subjectOf} phaseOf={phaseOf} />
      )}

      {view === 'week' && (<>
      {/* pickers */}
      <div className="flex flex-wrap items-center gap-2">
        {myTeacherId && <ModeTab active={mode === 'mine'} onClick={() => setMode('mine')}>My lessons</ModeTab>}
        {isParent && myChildren.length > 0 && <ModeTab active={mode === 'mine'} onClick={() => setMode('mine')}>My child</ModeTab>}
        {canPickClass && <ModeTab active={mode === 'class'} onClick={() => setMode('class')}>{canLookUp ? 'By class' : 'My classes'}</ModeTab>}
        {canLookUp && (
          <>
            <ModeTab active={mode === 'teacher'} onClick={() => setMode('teacher')}>By teacher</ModeTab>
            <ModeTab active={mode === 'room'} onClick={() => setMode('room')}>By room</ModeTab>
          </>
        )}

        <div className="flex-1" />

        {isParent && mode === 'mine' && myChildren.length > 1 && (
          <Select value={childId} onChange={setChildId} options={myChildren.map((s) => ({ value: s.id, label: s.fullName }))} />
        )}
        {mode === 'class' && (
          <Select value={pickedClass} onChange={setPickedClass} placeholder="Choose a class…"
                  options={classChoices.map((c) => ({ value: c.name, label: c.name }))} />
        )}
        {mode === 'teacher' && (
          <>
            <PhaseFilter value={phase} onChange={setPhase}
                         counts={{ all: teachers.length, primary: phaseCount('primary'), secondary: phaseCount('secondary'), islamic: phaseCount('islamic') }} />
            <Select value={pickedTeacher} onChange={setPickedTeacher} placeholder="Choose a teacher…"
                    options={teacherOptions} />
          </>
        )}
        {mode === 'room' && (
          <Select value={pickedRoom} onChange={setPickedRoom} placeholder="Choose a room…"
                  options={rooms.map((r) => ({ value: r, label: r }))} />
        )}
      </div>

      {error && (
        <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-[var(--sp-muted)]">Loading the timetable…</div>
      ) : !schedule ? (
        <Empty>No bell schedule has been published for this division yet.</Empty>
      ) : !grid ? (
        <Empty>
          {isParent
            ? 'Your child is not linked to a class with a timetable yet.'
            : myTeacherId || canLookUp
              ? 'Choose above to see a timetable.'
              : 'Your account is not linked to a teacher record, so there is no timetable to show.'}
        </Empty>
      ) : (
        <TimetableGrid
          grid={grid} schedule={schedule} heading={heading} subheading={subheading}
          pupil={pupil} focusTeacherId={focusTeacherId} preferNames={preferNames}
          codeOf={codeOf} nameOf={nameOf} subjectName={subjectName}
        />
      )}

      {/* What moved. Scoped the same way the grid is: a teacher sees changes to
          their own lessons, a parent to their child's class, and anyone who can
          look up any timetable can see the whole week's edits. */}
      {!loading && (
        <RecentChanges
          teacherId={focusTeacherId}
          className={
            focusTeacherId ? null
              : mode === 'class' ? (pickedClass || null)
              : isParent ? (childClass?.name ?? null)
              : null
          }
          showAll={canLookUp && !focusTeacherId && mode !== 'class' && !isParent}
        />
      )}
      </>)}
    </div>
    </Page>
  );
}

/* ==================== the grid ==================== */

/**
 * Days are ROWS, and each day carries its own period columns.
 *
 * This matters: the school does not run one bell schedule. Monday to Thursday
 * break after period 6; Friday breaks after period 4 and ends at period 8. A
 * single shared period axis would put Friday's period 5 under Monday's period 5
 * heading and its break in the wrong column — every Friday cell misaligned. So
 * each day gets its own header row, exactly as the printed timetable does.
 */
function TimetableGrid({
  grid, schedule, heading, subheading, pupil, focusTeacherId, preferNames,
  codeOf, nameOf, subjectName,
}: {
  grid: Grid;
  schedule: ScheduleSettings;
  heading: string;
  subheading: string;
  pupil: PupilContext | null;
  focusTeacherId: string | null;
  preferNames: boolean;
  codeOf: (id: string) => string | undefined;
  nameOf: (id: string) => string;
  subjectName: (id?: string | null) => string | undefined;
}) {
  const maxCols = Math.max(...DAYS.map((d) => (schedule.days?.[d] ?? []).length), 1);
  /* Said once, above the grid, rather than in every cell: a pupil needs to know
     why two lessons share a period, not be told thirty times. */
  const anyUnconfirmed = PERSONALISED_PUPIL_TIMETABLE && !!pupil && DAYS.some((d) => Object.values(grid[d] ?? {})
    .some((slot) => slot && groupsForStudent(slot, pupil).unconfirmed));

  return (
    <div className="rounded border border-[var(--sp-line)]">
      <div className="border-b border-[var(--sp-line)] px-4 py-3">
        <div className="font-semibold">{heading}</div>
        {subheading && <div className="text-sm text-[var(--sp-muted)]">{subheading}</div>}
        {anyUnconfirmed && (
          <div className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
            Where your set has not been confirmed yet — Physics L or M, say — both are shown,
            marked <span className="font-semibold">set not confirmed</span>. Your teacher will
            put you in one.
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 px-4 pt-3">
        {LEGEND_FAMILIES.map((f) => (
          <span key={f} className={`rounded-[3px] border px-2.5 py-1 text-[11px] font-medium ${FAM_TILE[f]}`}>
            {FAMILY_LABELS[f]}
          </span>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] border-separate text-sm" style={{ borderSpacing: '4px' }}>
          <tbody>
            {DAYS.map((day) => {
              const daySlots: PeriodSlot[] = schedule.days?.[day] ?? [];
              // A multi-period lesson is drawn once and spans its tail columns.
              const covered = new Set<number>();
              const cells: React.ReactNode[] = [];
              let used = 0;

              daySlots.forEach((ps, i) => {
                if (!isTeachingSlot(ps)) {
                  cells.push(
                    <td key={i} className="w-[26px] rounded-[3px] bg-[var(--fam-break)] px-0.5 py-3 text-center align-middle text-[10px] font-semibold uppercase tracking-wider text-[var(--sp-muted)]">
                      {ps.period === 'BREAK' ? 'Break' : 'Reg'}
                    </td>,
                  );
                  used += 1;
                  return;
                }
                if (covered.has(ps.period)) return;

                const slot = grid[day]?.[ps.period];
                if (!slot) {
                  cells.push(<td key={i} className="rounded-[3px] border border-dashed border-[var(--sp-line-2)] px-1 py-2" />);
                  used += 1;
                  return;
                }

                // Span only across CONSECUTIVE teaching columns — a break ends
                // the run even if the slot's period range continues past it.
                let span = 1;
                covered.add(ps.period);
                for (let j = i + 1; j < daySlots.length; j++) {
                  const nx = daySlots[j];
                  if (!isTeachingSlot(nx) || nx.period > slot.period_end) break;
                  covered.add(nx.period);
                  span += 1;
                }

                cells.push(
                  <td key={i} colSpan={span} className="h-px rounded-[3px] p-0 align-top">
                    <div className={`flex h-full min-h-[76px] flex-col gap-1 overflow-hidden rounded-[3px] border px-2 py-1.5 ${famTile(slot, subjectName)}`}>
                      <Cell slot={slot} pupil={pupil} focusTeacherId={focusTeacherId} preferNames={preferNames}
                            codeOf={codeOf} nameOf={nameOf} subjectName={subjectName} />
                    </div>
                  </td>,
                );
                used += span;
              });

              // Short days (Friday) stop early; pad so the table stays square.
              if (used < maxCols) {
                cells.push(<td key="pad" colSpan={maxCols - used} className="rounded-[3px] bg-[var(--sp-paper)]" />);
              }

              return (
                <Fragment key={day}>
                  <tr>
                    <th className={`w-24 border-b border-[var(--sp-line)] pb-1 pr-2 text-left align-bottom text-[11px] font-semibold ${day !== DAYS[0] ? 'pt-4' : ''}`}>
                      <span className="sr-only">{day}</span>
                    </th>
                    {daySlots.map((ps, i) => (
                      <th key={i} className={`border-b border-[var(--sp-line)] py-1 pb-1 text-left align-bottom text-[11px] font-semibold whitespace-nowrap ${day !== DAYS[0] ? 'pt-4' : ''} ${isTeachingSlot(ps) ? 'px-1' : 'w-[26px] px-0.5 text-center text-[var(--sp-muted)]'}`}>
                        <div>{isTeachingSlot(ps) ? ps.period : ps.period === 'BREAK' ? 'Break' : 'Reg'}</div>
                        <div className="block text-[9.5px] font-normal tabular-nums text-[var(--sp-muted)]">{ps.time.replace('-', '–')}</div>
                      </th>
                    ))}
                    {daySlots.length < maxCols && <th className="border-b border-[var(--sp-line)]" colSpan={maxCols - daySlots.length} />}
                  </tr>
                  <tr>
                    <th scope="row" className="w-24 whitespace-nowrap pr-2 text-left align-middle text-sm font-semibold">
                      {day}
                    </th>
                    {cells}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Tailwind cannot see a class it does not read literally, so the six families
   are listed out rather than built from the key. A lesson showing several
   subjects at once is an option block and takes the block colour. */
const FAM_TILE: Record<SubjectFamily, string> = {
  lang:  'bg-[var(--fam-lang-bg)]  text-[var(--fam-lang-fg)]  border-[var(--fam-lang-br)]',
  math:  'bg-[var(--fam-math-bg)]  text-[var(--fam-math-fg)]  border-[var(--fam-math-br)]',
  sci:   'bg-[var(--fam-sci-bg)]   text-[var(--fam-sci-fg)]   border-[var(--fam-sci-br)]',
  hum:   'bg-[var(--fam-hum-bg)]   text-[var(--fam-hum-fg)]   border-[var(--fam-hum-br)]',
  opt:   'bg-[var(--fam-opt-bg)]   text-[var(--fam-opt-fg)]   border-[var(--fam-opt-br)]',
  other: 'bg-[var(--fam-other-bg)] text-[var(--fam-other-fg)] border-[var(--fam-other-br)]',
};

function famTile(slot: TimetableSlot, subjectName: (id?: string | null) => string | undefined): string {
  const groups = slot.groups ?? [];
  const named = groups.map((g) => subjectName(g.subjectId) ?? g.subjectLabel ?? '');
  const distinct = new Set(named.map((n) => n.toLowerCase()).filter(Boolean));
  if (distinct.size > 1) return FAM_TILE.opt;
  return FAM_TILE[subjectFamily(named[0])];
}

function Cell({
  slot, pupil, focusTeacherId, preferNames, codeOf, nameOf, subjectName,
}: {
  slot: TimetableSlot;
  pupil: PupilContext | null;
  focusTeacherId: string | null;
  preferNames: boolean;
  codeOf: (id: string) => string | undefined;
  nameOf: (id: string) => string;
  subjectName: (id?: string | null) => string | undefined;
}) {
  // On a teacher's timetable, show only THEIR group of an option block —
  // otherwise colleagues teaching other subjects in the same period read as
  // though they were in the room together.
  const picked = focusTeacherId
    ? { ...groupsForTeacher(slot, focusTeacherId), unconfirmed: false }
    : groupsForStudent(slot, pupil ?? { subjectIds: [], streams: {} });
  const { groups, narrowed, unconfirmed } = picked;
  const span = slotLength(slot);
  const others = focusTeacherId ? coTeachers(groups, focusTeacherId) : [];

  return (
    <div className="space-y-1">
      {groups.map((g, i) => (
        <GroupLine key={i} g={g} focusTeacherId={focusTeacherId} preferNames={preferNames}
                   codeOf={codeOf} nameOf={nameOf} subjectName={subjectName} />
      ))}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] opacity-75">
        {span > 1 && <span>{span} periods</span>}
        {slot.classes?.length > 0 && <span>· {slot.classes.join(', ')}</span>}
        {others.length > 0 && <span>· with {others.map((t) => codeOf(t) || nameOf(t)).join(', ')}</span>}
        {groups.length > 1 && !narrowed && <span>· option block</span>}
        {PERSONALISED_PUPIL_TIMETABLE && unconfirmed && (
          <span className="font-semibold text-amber-700 dark:text-amber-400">· set not confirmed</span>
        )}
      </div>
    </div>
  );
}

function GroupLine({
  g, focusTeacherId, preferNames, codeOf, nameOf, subjectName,
}: {
  g: SlotGroup;
  focusTeacherId: string | null;
  preferNames: boolean;
  codeOf: (id: string) => string | undefined;
  nameOf: (id: string) => string;
  subjectName: (id?: string | null) => string | undefined;
}) {
  // On a teacher's own grid the teacher themself is implied — listing their own
  // code in every cell is noise. Co-teachers are surfaced on the meta line.
  const ids = focusTeacherId
    ? (g.teacherIds ?? []).filter((t) => t !== focusTeacherId)
    : (g.teacherIds ?? []);
  const teacherText = [
    // A pupil's names arrive from a callable; until they do (or if it fails)
    // show nothing rather than an internal id like "teacher_1771849458136".
    ...ids.map((t) => (preferNames ? nameOf(t) : codeOf(t) || nameOf(t)))
      .filter((n) => !(preferNames && /^teacher_\d+$/.test(n))),
    ...(g.unresolvedTeachers ?? []).map((u) => u.code || u.name || '?'),
  ].join(' + ');

  const isSupervision = g.kind === 'supervision';
  const label = isSupervision
    ? 'Supervised study'
    : subjectName(g.subjectId) ?? tidySubjectLabel(g.subjectLabel, g.streamLabel) ?? '—';

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className={isSupervision ? 'italic opacity-75' : 'font-medium'}>{label}</span>
        {g.streamLabel && <span className="text-[11px] opacity-75">{g.streamLabel}</span>}
        {g.kind === 'enrichment' && (
          <span className="rounded bg-black/10 px-1 text-[10px] uppercase tracking-wide opacity-80">
            not examined
          </span>
        )}
        {g.subjectUnmapped && (
          <span title="This subject has not been matched to a portal subject yet." className="text-amber-500">
            <AlertTriangle className="inline h-3 w-3" />
          </span>
        )}
      </div>
      {preferNames ? (
        /* Pupils and parents: who and where are what they read the grid for,
           so they get their own lines at a readable size. */
        <div className="text-xs opacity-90">
          {teacherText && <div>{teacherText}</div>}
          {g.room && <div className="font-medium">Room {g.room}</div>}
        </div>
      ) : (
        <div className="text-[11px] opacity-75">
          {teacherText}
          {g.room ? `${teacherText ? ' · ' : ''}${g.room}` : ''}
        </div>
      )}
    </div>
  );
}

/**
 * The authoring app's label ("Physics_L", "Mathematics_1",
 * "Environmental_Management_") for screens that cannot look the subject up —
 * a student session loads no subjects. The set is shown beside it already.
 */
/* ==================== bits ==================== */

function ModeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded px-3 py-1.5 text-sm ${
        active
          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
          : 'border border-[var(--sp-line-2)] hover:bg-[var(--sp-paper)]'
      }`}
    >
      {children}
    </button>
  );
}

/** All · Primary · Secondary · Islamic, with how many each holds. A teacher
 *  in two parts of the school is counted in both. */
function PhaseFilter({
  value, onChange, counts,
}: {
  value: 'all' | TeacherPhase;
  onChange: (v: 'all' | TeacherPhase) => void;
  counts: Record<'all' | TeacherPhase, number>;
}) {
  const opts: { v: 'all' | TeacherPhase; label: string }[] = [
    { v: 'all', label: 'All' }, { v: 'primary', label: 'Primary' }, { v: 'secondary', label: 'Secondary' }, { v: 'islamic', label: 'Islamic' },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded border border-[var(--sp-line-2)] text-sm"
         role="group" aria-label="Which teachers">
      {opts.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} aria-pressed={value === o.v}
          className={`px-2.5 py-1.5 ${value === o.v
            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
            : 'hover:bg-[var(--sp-paper)]'}`}>
          {o.label} <span className="tabular-nums opacity-60">{counts[o.v]}</span>
        </button>
      ))}
    </div>
  );
}

function Select({
  value, onChange, options, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border border-[var(--sp-line-2)] bg-[var(--sp-card)] px-2 py-1.5 text-sm"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded border border-dashed border-[var(--sp-line-2)] px-6 py-16 text-center text-sm text-[var(--sp-muted)]">
      {children}
    </div>
  );
}
